import { storage } from "utils/firebase";

/**
 * Upload a logo image to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} path - The storage path (e.g., 'campaign-logos/campaign-id/logo.png')
 * @returns {Promise<string>} - The public URL of the uploaded file
 */
export const uploadLogo = async (file, path) => {
  try {
    if (!file) {
      throw new Error("No file provided");
    }

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      throw new Error("Invalid file type. Please upload a JPG, PNG, or WebP image.");
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error("File size exceeds 5MB limit.");
    }

    // Create a storage reference
    const storageRef = storage.ref();
    const logoRef = storageRef.child(path);

    // Upload file
    const snapshot = await logoRef.put(file);

    // Get public URL
    const downloadURL = await snapshot.ref.getDownloadURL();

    return downloadURL;
  } catch (error) {
    console.error("Error uploading logo:", error);
    throw error;
  }
};

/**
 * Delete a logo from Firebase Storage
 * @param {string} url - The public URL of the file to delete
 */
export const deleteLogo = async (url) => {
  try {
    if (!url) return;

    const storageRef = storage.refFromURL(url);
    await storageRef.delete();
  } catch (error) {
    console.error("Error deleting logo:", error);
    throw error;
  }
};

/**
 * Generate a unique path for storing campaign logos
 * @param {string} campaignId - The campaign ID
 * @param {string} logoType - The type of logo ('foreverx' or 'store')
 * @returns {string} - The storage path
 */
export const generateLogoPath = (campaignId, logoType) => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 8);
  return `campaigns/${campaignId}/${logoType}-${timestamp}-${randomString}`;
};
